import {cn} from "@/lib/utils.ts";
import services from "@/services";
import {Player} from "@/types";
import {useDebounceEffect} from "@/utilities/useDebounceEffect.tsx";
import {Card, CardBody, CardFooter} from "@heroui/react";
import React, {useRef, useState} from 'react'

import ReactCrop, {centerCrop, Crop, makeAspectCrop, PixelCrop} from 'react-image-crop'

import 'react-image-crop/dist/ReactCrop.css'

const TO_RADIANS = Math.PI / 180

export async function imageCropCanvasPreview(
    image: HTMLImageElement,
    canvas: HTMLCanvasElement,
    crop: PixelCrop,
    scale = 1,
    rotate = 0,
) {
    const ctx = canvas.getContext('2d')

    if (!ctx) {
        throw new Error('No 2d context')
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    // devicePixelRatio slightly increases sharpness on retina devices
    // at the expense of slightly slower render times and needing to
    // size the image back down if you want to download/upload and be
    // true to the images natural size.
    const pixelRatio = window.devicePixelRatio
    // const pixelRatio = 1

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio)
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio)

    ctx.scale(pixelRatio, pixelRatio)
    ctx.imageSmoothingQuality = 'high'

    const cropX = crop.x * scaleX
    const cropY = crop.y * scaleY

    const rotateRads = rotate * TO_RADIANS
    const centerX = image.naturalWidth / 2
    const centerY = image.naturalHeight / 2

    ctx.save()

    // 5) Move the crop origin to the canvas origin (0,0)
    ctx.translate(-cropX, -cropY)
    // 4) Move the origin to the center of the original position
    ctx.translate(centerX, centerY)
    // 3) Rotate around the origin
    ctx.rotate(rotateRads)
    // 2) Scale the image
    ctx.scale(scale, scale)
    // 1) Move the center of the image to the origin (0,0)
    ctx.translate(-centerX, -centerY)
    ctx.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
    )

    ctx.restore()
}

function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit:  '%',
                width: 100,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    )
}

export default function ImageCrop({imgSrc, onChange, profile}: { imgSrc: string, profile: Player, onChange: (crop: any) => void }) {
    const previewCanvasRef = useRef<HTMLCanvasElement>(null)
    const imgRef = useRef<HTMLImageElement>(null)
    const hiddenAnchorRef = useRef<HTMLAnchorElement>(null)
    const blobUrlRef = useRef('')
    const [crop, setCrop] = useState<Crop>()
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
    const [scale, setScale] = useState(1)
    const [aspect, setAspect] = useState<number | undefined>(1)

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        if (aspect) {
            const {width, height} = e.currentTarget
            setCrop(centerAspectCrop(width, height, aspect))
        }
    }

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                // We use canvasPreview as it's much faster than imgPreview.
                imageCropCanvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                )
            }
        },
        100,
        [completedCrop, scale],
    )

    return (
        <div className="grid gap-4 grid-cols-2 max-lg:grid-cols-1">
            <div>
                <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => {
                        setCrop(percentCrop);
                        onChange(percentCrop);
                    }}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={aspect}
                    minWidth={50}
                    minHeight={50}
                    maxWidth={600}
                    maxHeight={600}
                    // circularCrop
                >
                    <img
                        className={"max-h-128!"}
                        ref={imgRef}
                        alt="Crop me"
                        src={imgSrc}
                        onLoad={onImageLoad}
                    />
                </ReactCrop>
            </div>
            <div>
                {!!completedCrop && (<>
                        Final result:<br />
                        <Card className={cn("aspect-square inline-block w-auto")}>
                            <CardBody className={"p-0 min-w-32 h-48 w-48 overflow-hidden"}>
                                <canvas
                                    ref={previewCanvasRef}
                                    style={{
                                        border:    '1px solid black',
                                        objectFit: 'cover',
                                        // width:     completedCrop.width,
                                        // height:    completedCrop.height,
                                    }}
                                />
                            </CardBody>
                            <CardFooter className="absolute bottom-1 z-10">
                                <div className={"text-white w-full justify-between border-white/20 border-1 overflow-hidden bg-black/20 rounded-large  shadow-small backdrop-blur-sm p-1 px-2"}>
                                    <h2 className="font-semibold mb-1 whitespace-nowrap overflow-hidden text-ellipsis">{profile.name}</h2>
                                </div>
                            </CardFooter>
                        </Card>
                    </>
                )}
            </div>
        </div>
    )
}
